/**
 * FeedbackButton (#504 D4-FBK-010; brightmark annotate mode #546, epic #543).
 *
 * Always-visible top-bar entry point for in-app feedback. Mounted in BOTH AdminLayout and CrmLayout,
 * so it appears on every dashboard surface — admin, agent, and the gated /legal + /finance shells
 * (which reuse CrmLayout). Layout-agnostic: it carries its own state and needs no props.
 *
 * The single icon button now opens a small popover with three actions:
 *  1. Point at the problem  → brightmark annotate mode (point-and-click / text-select capture)
 *  2. Write a report        → the classic FeedbackModal, unchanged
 *  3. My reports            → navigates to the reports list for the current dashboard
 *
 * Annotate mode lazy-loads the brightmark capture library (`lib/brightmark/`, which pulls in snapdom)
 * via dynamic import() on first use, mounts an AnnotateOverlay, and on capture opens the modal
 * pre-filled with an annotation chip + the element screenshot. Realtor-simple, >=44px touch targets,
 * theme tokens, no GitHub jargon. The popover/overlay/toast carry `data-brightmark-ui` so they are
 * excluded from capture target resolution.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ListChecks, MessageSquarePlus, MousePointerClick } from "lucide-react";
import { FeedbackModal } from "./FeedbackModal";
import { AnnotateOverlay } from "./annotate/AnnotateOverlay";
import { loadBrightmark, type BrightmarkLib } from "./annotate/brightmarkLazy";
import { isTypingTarget, matchesFeedbackShortcut } from "./shortcut";
import { useTour } from "../../contexts/tour";
import { buildTourContext } from "../../lib/brightmark/tourContext";
import { onOpenFeedback, type OpenFeedbackPayload } from "../../lib/feedback/openFeedback";
import type { FeedbackType } from "../../api/feedback";
import type { BrightmarkAnnotation } from "../../lib/brightmark/types";

type Mode = "idle" | "annotating";

export function FeedbackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const tour = useTour();

  const [open, setOpen] = useState(false); // modal open
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("idle");
  const [isMac, setIsMac] = useState(true);
  const [lib, setLib] = useState<BrightmarkLib | null>(null);
  const [loadingLib, setLoadingLib] = useState(false);

  // Captured annotation + its (async) screenshot, threaded into the modal.
  const [annotation, setAnnotation] = useState<BrightmarkAnnotation | null>(null);
  const [shotMedia, setShotMedia] = useState<Blob | null>(null);
  // Pre-fill from a programmatic open (help assistant, #3604); cleared by the classic paths.
  const [prefill, setPrefill] = useState<{ type: FeedbackType; description: string }>({
    type: "bug",
    description: "",
  });

  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Show the right modifier glyph for the platform (mirrors HeaderSearchButton).
  useEffect(() => {
    setIsMac(/Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent));
  }, []);

  // Close the popover on outside-click / Escape.
  useEffect(() => {
    if (!popoverOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPopoverOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [popoverOpen]);

  // Global launch shortcut: Ctrl/Cmd + Shift + F opens the annotate flow from
  // anywhere in either shell (#1979, updated #2008). Mirrors GlobalSearchProvider's
  // window-level keydown pattern; inert while typing and when annotate/modal is
  // already up. Refs keep the (stable) listener reading live state without re-binding.
  const modeRef = useRef(mode);
  const openRef = useRef(open);
  const loadingRef = useRef(loadingLib);
  const isMacRef = useRef(isMac);
  modeRef.current = mode;
  openRef.current = open;
  loadingRef.current = loadingLib;
  isMacRef.current = isMac;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!matchesFeedbackShortcut(e, isMacRef.current)) return;
      if (isTypingTarget(e.target) || isTypingTarget(document.activeElement)) return;
      // Don't re-enter while already capturing / a report modal is open / loading.
      if (modeRef.current === "annotating" || openRef.current || loadingRef.current) return;
      e.preventDefault();
      void startAnnotate();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Programmatic open (#3604): other surfaces dispatch OPEN_FEEDBACK_EVENT with an
  // optional pre-fill instead of reaching into this component's state.
  useEffect(
    () =>
      onOpenFeedback((payload: OpenFeedbackPayload) => {
        setPopoverOpen(false);
        setMode("idle");
        setAnnotation(null);
        setShotMedia(null);
        setPrefill({ type: payload.type ?? "bug", description: payload.description ?? "" });
        setOpen(true);
      }),
    [],
  );

  async function startAnnotate() {
    setPopoverOpen(false);
    setLoadingLib(true);
    try {
      const loaded = await loadBrightmark();
      setLib(loaded);
      setMode("annotating");
    } catch {
      // Lazy-load failed (offline / chunk error) — fall back to the classic report flow.
      openClassic();
    } finally {
      setLoadingLib(false);
    }
  }

  function openClassic() {
    setPopoverOpen(false);
    setPrefill({ type: "bug", description: "" });
    setAnnotation(null);
    setShotMedia(null);
    setOpen(true);
  }

  function goToReports() {
    setPopoverOpen(false);
    const base = location.pathname.startsWith("/admin") ? "/admin" : "/crm";
    navigate(`${base}/feedback/reports`);
  }

  // Capture complete: build the annotation immediately, open the pre-filled modal, and fire the
  // (best-effort, async) snapdom screenshot. The anchor is durable; the raster follows when ready.
  function handleCaptured(captured: BrightmarkAnnotation, target: Element) {
    setMode("idle");
    // If captured during a guided tour, pin the work order to the exact step (#1979).
    const tourCtx = buildTourContext(tour);
    const withTour = tourCtx ? { ...captured, tour: tourCtx } : captured;
    setAnnotation(withTour);
    setShotMedia(null);
    setPrefill({ type: "bug", description: "" });
    setOpen(true);

    if (lib) {
      lib
        .captureElement(target)
        .then((shot) => {
          if (!shot) return;
          try {
            setShotMedia(lib.dataUrlToBlob(shot.data_url));
          } catch {
            /* screenshot best-effort — ignore a malformed data URL */
          }
        })
        .catch(() => {
          /* taint / failure tolerated — submission proceeds without a screenshot */
        });
    }
  }

  function cancelAnnotate() {
    setMode("idle");
  }

  function discardAnnotation() {
    setAnnotation(null);
    setShotMedia(null);
  }

  // Platform-aware shortcut hint shown in the button tooltip/aria-label (#1979,
  // updated #2008), mirroring how the search button surfaces "⌘ K".
  const shortcutLabel = isMac ? "⌘⇧F" : "Ctrl+Shift+F";

  return (
    <>
      <div ref={wrapRef} className="relative" data-brightmark-ui="popover-root" data-tour="header-feedback">
        <button
          onClick={() => setPopoverOpen((v) => !v)}
          aria-label={`Submit feedback (${shortcutLabel})`}
          aria-haspopup="menu"
          aria-expanded={popoverOpen}
          title={`Send feedback (${shortcutLabel})`}
          disabled={loadingLib}
          className="inline-flex items-center gap-2 min-h-[44px] px-3 rounded-lg border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-page)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <MessageSquarePlus className="h-5 w-5" />
          <span className="hidden sm:inline">Feedback</span>
        </button>

        {popoverOpen && (
          <div
            role="menu"
            data-brightmark-ui="popover"
            className="fixed left-4 right-4 sm:absolute sm:left-auto sm:right-0 mt-2 sm:w-60 z-50 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-xl p-1.5"
          >
            <button
              role="menuitem"
              type="button"
              onClick={startAnnotate}
              className="flex w-full items-start gap-3 min-h-[44px] rounded-md px-3 py-2 text-left hover:bg-[var(--color-bg-page)]"
            >
              <MousePointerClick className="h-5 w-5 mt-0.5 text-[var(--color-accent)] shrink-0" />
              <span>
                <span className="block text-sm font-medium text-[var(--color-text-primary)]">
                  Point at the problem
                </span>
                <span className="block text-xs text-[var(--color-text-muted)]">
                  Click or select what looks wrong
                </span>
              </span>
            </button>
            <button
              role="menuitem"
              type="button"
              onClick={openClassic}
              className="flex w-full items-start gap-3 min-h-[44px] rounded-md px-3 py-2 text-left hover:bg-[var(--color-bg-page)]"
            >
              <MessageSquarePlus className="h-5 w-5 mt-0.5 text-[var(--color-accent)] shrink-0" />
              <span>
                <span className="block text-sm font-medium text-[var(--color-text-primary)]">
                  Write a report
                </span>
                <span className="block text-xs text-[var(--color-text-muted)]">
                  Describe it in your own words
                </span>
              </span>
            </button>
            <button
              role="menuitem"
              type="button"
              onClick={goToReports}
              className="flex w-full items-start gap-3 min-h-[44px] rounded-md px-3 py-2 text-left hover:bg-[var(--color-bg-page)]"
            >
              <ListChecks className="h-5 w-5 mt-0.5 text-[var(--color-accent)] shrink-0" />
              <span>
                <span className="block text-sm font-medium text-[var(--color-text-primary)]">
                  My reports
                </span>
                <span className="block text-xs text-[var(--color-text-muted)]">
                  See what you've sent
                </span>
              </span>
            </button>
          </div>
        )}
      </div>

      {mode === "annotating" && lib && (
        <AnnotateOverlay
          lib={lib}
          onCaptured={handleCaptured}
          onCancel={cancelAnnotate}
          tourActive={tour.isRunning}
        />
      )}

      <FeedbackModal
        open={open}
        onClose={() => setOpen(false)}
        annotation={annotation}
        initialType={prefill.type}
        initialDescription={prefill.description}
        initialMedia={shotMedia}
        initialMediaName="element-screenshot.png"
        onDiscardAnnotation={discardAnnotation}
      />
    </>
  );
}
